import React, { useMemo, useState } from "react";
import { MdCampaign, MdOpenInNew } from "react-icons/md";
import { admission } from "../config/api";
import "./NewsList.css";

function newsFileHref(path) {
  if (!path) return "#";
  if (/^https?:\/\//i.test(path)) return path;
  const p = String(path).replace(/^\//, "");
  return `${admission}/${p}`;
}

function formatNewsDate(item) {
  if (item?.datetime) return String(item.datetime);
  const raw = item?.NEWS_DATE;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Shared public-relations news list.
 * @param {object} props
 * @param {Array} props.items
 * @param {string} [props.subtitle]
 * @param {number} [props.previewCount] — if set, show N then expand/collapse or moreHref
 * @param {string} [props.moreHref] — link to full news page (preferred over expand)
 * @param {boolean} [props.collapsible] — expand in-place when no moreHref
 * @param {boolean} [props.pageLayout] — full news page spacing
 * @param {string} [props.emptyText]
 */
export default function NewsList({
  items = [],
  subtitle = "",
  previewCount,
  moreHref = "",
  collapsible = false,
  pageLayout = false,
  emptyText = "ยังไม่มีข่าวประชาสัมพันธ์ในขณะนี้",
}) {
  const [expanded, setExpanded] = useState(false);
  const list = Array.isArray(items) ? items : [];

  const visible = useMemo(() => {
    if (!previewCount) return list;
    if (moreHref) return list.slice(0, previewCount);
    if (expanded) return list;
    return list.slice(0, previewCount);
  }, [list, previewCount, expanded, moreHref]);

  const showMoreLink = Boolean(moreHref) && list.length > (previewCount || 0);
  const showExpand =
    collapsible && !moreHref && Boolean(previewCount) && list.length > previewCount;

  return (
    <section
      className={`kpru-news${pageLayout ? " kpru-news--page" : ""}`}
      aria-label="ข่าวประชาสัมพันธ์"
    >
      <div className="kpru-news__panel">
        <div className="kpru-news__head">
          <h2 className="kpru-news__badge">
            <MdCampaign size={18} aria-hidden />
            ข่าวประชาสัมพันธ์
          </h2>
          {subtitle ? <p className="kpru-news__subtitle">{subtitle}</p> : null}
        </div>

        {list.length === 0 ? (
          <p className="kpru-news__empty">{emptyText}</p>
        ) : (
          <>
            <ul className="kpru-news__list">
              {visible.map((item, index) => {
                const title = item.NEWS_TITEL || item.title || "";
                const href = newsFileHref(item.NEWS_FILE || item.href);
                const dateLabel = formatNewsDate(item);
                const isNew = index === 0 && (!previewCount || !expanded);

                return (
                  <li
                    key={item.NEWS_FILE || item.NEWS_ID || `${title}-${index}`}
                    className={`kpru-news__item${isNew ? " is-new" : ""}`}
                  >
                    <div className="kpru-news__body">
                      <div className="kpru-news__meta">
                        {isNew ? <span className="kpru-news__tag">ใหม่</span> : null}
                        {dateLabel ? (
                          <time className="kpru-news__date" dateTime={item.NEWS_DATE || undefined}>
                            ข้อมูล ณ วันที่ {dateLabel}
                          </time>
                        ) : null}
                      </div>
                      <p className="kpru-news__title">{title}</p>
                    </div>
                    <a
                      className="kpru-news__action"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      อ่านเอกสาร
                      <MdOpenInNew size={16} aria-hidden />
                    </a>
                  </li>
                );
              })}
            </ul>

            {showMoreLink || showExpand ? (
              <div className="kpru-news__footer">
                {showMoreLink ? (
                  <a className="kpru-news__more" href={moreHref}>
                    ดูข่าวทั้งหมด ({list.length})
                  </a>
                ) : (
                  <button
                    type="button"
                    className="kpru-news__more"
                    onClick={() => setExpanded((v) => !v)}
                  >
                    {expanded ? "แสดงน้อยลง" : `ดูเพิ่มเติม (${list.length})`}
                  </button>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
